import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { differenceInCalendarDays, format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { FeatureGate } from '../../../../components/FeatureGate';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { formatName } from '../../../../utils/formatName';
import {
  labelLevelAccess,
  resolveAccessLevel,
} from '../../../../utils/labelLeverAccess';

import noAvatar from '../../../../assets/no-avatar.svg';

import {
  BadgeRow,
  BillingBadge,
  Container,
  ContainerAvatar,
  ContentGrid,
  GaugeFill,
  GaugeTrack,
  Header,
  LevelBadge,
  MainColumn,
  NameRow,
  NavCard,
  NavGrid,
  NavIcon,
  NavLabel,
  Sidebar,
  SectionTitle,
  StatusBadge,
  StudentName,
  SummaryCard,
  SummaryCardRow,
  SummaryLabel,
  SummaryRow,
  SummaryValue,
  WeekBarColumn,
  WeekBarFill,
  WeekBarLabel,
  WeekBarsRow,
  WeekBarTrack,
} from './styles';

interface IUserProps {
  id: string;
  fullname: string;
  avatar_url: string | undefined;
  permissions: string[];
  is_active: boolean;
  membership_status?: string;
}

interface IBillingStatus {
  status: 'ok' | 'pending' | 'overdue' | 'not_informed';
  days_overdue?: number;
  amount?: number;
  next_due_date?: string;
}

interface IWeekProgressItem {
  index: number;
  progress?: number;
  day: { title: string };
}

interface IExpirationStatus {
  is_expired: boolean;
  is_expiring_soon: boolean;
  expires_at: string;
  percent_elapsed: number;
}

const billingLabel: Record<string, string> = {
  ok: 'Em dia',
  pending: 'Aguardando',
  not_informed: 'Não informado',
};

const WEEK_DAY_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<IUserProps>();
  const [lastTraining, setLastTraining] = useState<string>();
  const [billingStatus, setBillingStatus] = useState<IBillingStatus>();
  const [weekProgress, setWeekProgress] = useState<IWeekProgressItem[]>([]);
  const [expiration, setExpiration] = useState<IExpirationStatus | null>(null);

  const getUserData = useCallback(() => {
    api
      .get(`users/gym/${id}`)
      .then(response => {
        setUser(response.data.user);
        setBillingStatus(response.data.billingStatus);
        setLastTraining(response.data.lastTraining);
      })
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos dados do usuário.',
          description: err?.response?.status,
        });
      })
      .finally(() => setLoading(false));
  }, [addToast, id]);

  const getWeekProgress = useCallback(() => {
    if (!id) return;

    api
      .get(`trainings/gym/${id}/weekly-progress`)
      .then(response => setWeekProgress(response.data.progress || []))
      .catch(() => setWeekProgress([]));
  }, [id]);

  const getExpiration = useCallback(() => {
    if (!id) return;

    api
      .get(`trainings/gym/${id}/expiration`)
      .then(response => setExpiration(response.data))
      .catch(() => setExpiration(null));
  }, [id]);

  useEffect(() => {
    getUserData();
    getWeekProgress();
    getExpiration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const levelLabel = labelLevelAccess(user?.permissions || []);
  const today = new Date().getDay();

  const daysUntilExpiration = expiration
    ? differenceInCalendarDays(new Date(expiration.expires_at), new Date())
    : undefined;

  const expirationTone: 'ok' | 'warning' | 'danger' = expiration?.is_expired
    ? 'danger'
    : expiration?.is_expiring_soon
    ? 'warning'
    : 'ok';

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <ContainerAvatar>
            <img src={user?.avatar_url || noAvatar} alt="avatar do aluno" />
          </ContainerAvatar>

          <div>
            <NameRow>
              <StudentName>{formatName(user?.fullname || '')}</StudentName>
            </NameRow>

            <BadgeRow>
              <StatusBadge
                status={
                  user?.is_active === false
                    ? 'inactive'
                    : user?.membership_status === 'pending'
                    ? 'pending'
                    : 'active'
                }
              >
                {user?.is_active === false
                  ? 'Inativo'
                  : user?.membership_status === 'pending'
                  ? 'Pendente'
                  : 'Ativo'}
              </StatusBadge>
              <LevelBadge level={resolveAccessLevel(user?.permissions || [])}>
                {levelLabel}
              </LevelBadge>
            </BadgeRow>
          </div>
        </Header>

        <ContentGrid>
          <MainColumn>
            <SummaryRow>
              <SummaryCard>
                <SummaryLabel>Última sessão</SummaryLabel>
                <SummaryValue>
                  {lastTraining
                    ? `${format(new Date(lastTraining), 'dd/MM/yyyy')} · há ${formatDistanceToNow(
                        new Date(lastTraining),
                        { locale: ptBR },
                      )}`
                    : 'Sem dados'}
                </SummaryValue>
              </SummaryCard>

              <SummaryCard>
                <SummaryLabel>Progresso semanal</SummaryLabel>
                <WeekBarsRow>
                  {WEEK_DAY_LABELS.map((label, index) => {
                    const item = weekProgress.find(p => p.index === index);

                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <WeekBarColumn key={index}>
                        <WeekBarTrack active={index === today}>
                          <WeekBarFill
                            percent={item?.progress || 0}
                            active={index === today}
                          />
                        </WeekBarTrack>
                        <WeekBarLabel active={index === today}>
                          {label}
                        </WeekBarLabel>
                      </WeekBarColumn>
                    );
                  })}
                </WeekBarsRow>
              </SummaryCard>
            </SummaryRow>

            <SummaryCard>
              <SummaryLabel>Situação financeira</SummaryLabel>
              <FeatureGate requiredFeature="student_billing">
                {billingStatus ? (
                  <>
                    <BillingBadge status={billingStatus.status}>
                      {billingStatus.status === 'overdue'
                        ? `Atrasado (${billingStatus.days_overdue}d)`
                        : billingLabel[billingStatus.status]}
                    </BillingBadge>

                    {billingStatus.next_due_date && (
                      <SummaryCardRow>
                        <span>Próximo vencimento</span>
                        <strong>
                          {format(
                            new Date(billingStatus.next_due_date),
                            'dd/MM/yyyy',
                          )}
                        </strong>
                      </SummaryCardRow>
                    )}
                    {billingStatus.amount !== undefined && (
                      <SummaryCardRow>
                        <span>Valor</span>
                        <strong>
                          {currencyFormatter.format(billingStatus.amount)}
                        </strong>
                      </SummaryCardRow>
                    )}
                  </>
                ) : (
                  <SummaryValue>—</SummaryValue>
                )}
              </FeatureGate>
            </SummaryCard>

            <SummaryCard>
              <SummaryLabel>Progresso da ficha atual</SummaryLabel>
              <SummaryValue>
                {expiration
                  ? expiration.is_expired
                    ? `Expirada há ${Math.abs(daysUntilExpiration || 0)} dia(s)`
                    : `Faltam ${daysUntilExpiration} dia(s) pra expirar`
                  : '—'}
              </SummaryValue>
              {!!expiration && (
                <GaugeTrack>
                  <GaugeFill
                    percent={expiration.percent_elapsed}
                    tone={expirationTone}
                  />
                </GaugeTrack>
              )}
            </SummaryCard>
          </MainColumn>

          <Sidebar>
            <SectionTitle>Acesso rápido</SectionTitle>
            <NavGrid>
              <NavCard type="button" onClick={() => navigate(`/users/${id}/0`)}>
                <NavIcon>
                  <Icon name="assignment" size={22} color={colors.primary} />
                </NavIcon>
                <NavLabel>Ficha de treino</NavLabel>
              </NavCard>

              <NavCard
                type="button"
                onClick={() => navigate(`/users/${id}/measurements`)}
              >
                <NavIcon>
                  <Icon name="straighten" size={22} color={colors.primary} />
                </NavIcon>
                <NavLabel>Medidas</NavLabel>
              </NavCard>

              <NavCard
                type="button"
                onClick={() => navigate(`/users/${id}/dues`)}
              >
                <NavIcon>
                  <Icon name="payments" size={22} color={colors.primary} />
                </NavIcon>
                <NavLabel>Mensalidade</NavLabel>
              </NavCard>

              <NavCard
                type="button"
                onClick={() => navigate(`/users/${id}/permissions`)}
              >
                <NavIcon>
                  <Icon
                    name="admin_panel_settings"
                    size={22}
                    color={colors.primary}
                  />
                </NavIcon>
                <NavLabel>Permissões</NavLabel>
              </NavCard>
            </NavGrid>
          </Sidebar>
        </ContentGrid>
      </Container>
    </Painel>
  );
};

export { UserDetails };
